import { FileEdit, MessageSquare, FileText, ArrowRight, Sparkles, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';

interface DraftsTaskGuideProps {
  onCloseWizard: () => void;
  onNavigateToDrafts: () => void;
}

export function DraftsTaskGuide({ onCloseWizard, onNavigateToDrafts }: DraftsTaskGuideProps) {
  const handleGetStarted = () => {
    onNavigateToDrafts();
    onCloseWizard();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mx-auto">
          <FileEdit className="w-8 h-8 text-pink-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Draft Documents with AI</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Create professional patent documents, office action responses, and summaries with AI-powered assistance
        </p>
      </div>

      {/* How It Works */}
      <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-lg p-6 space-y-4">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-pink-600" />
          How It Works
        </h3>

        <div className="space-y-3">
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
              <span className="text-sm font-semibold text-pink-600">1</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Create a New Draft</p>
              <p className="text-sm text-gray-600">Click the draft icon (top right) and create a new draft document</p>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
              <span className="text-sm font-semibold text-pink-600">2</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Add to Active Documents</p>
              <p className="text-sm text-gray-600">Once added, the AI chat automatically has access to your draft content</p>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
              <span className="text-sm font-semibold text-pink-600">3</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Chat to Add Content</p>
              <p className="text-sm text-gray-600">Ask AI to draft sections, revise content, or add analysis directly to your draft</p>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
              <span className="text-sm font-semibold text-pink-600">4</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">Edit & Export</p>
              <p className="text-sm text-gray-600">Use the line-numbered editor to refine and export to DOCX when ready</p>
            </div>
          </div>
        </div>
      </div>

      {/* Use Cases */}
      <div className="space-y-3">
        <h3 className="font-semibold text-gray-900">Common Use Cases</h3>

        <div className="grid gap-3">
          {/* Patent Specification */}
          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 mb-1">Patent Specification Drafting</h4>
                <p className="text-sm text-gray-600 mb-2">
                  Create comprehensive patent specifications with AI assistance
                </p>
                <div className="bg-gray-50 rounded p-2 text-xs font-mono text-gray-700">
                  "Draft a background section for this invention based on the technical outline"
                </div>
              </div>
            </div>
          </div>

          {/* Office Action Response */}
          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <MessageSquare className="w-5 h-5 text-purple-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 mb-1">Office Action Responses</h4>
                <p className="text-sm text-gray-600 mb-2">
                  Draft comprehensive responses to office action rejections
                </p>
                <div className="bg-gray-50 rounded p-2 text-xs font-mono text-gray-700">
                  "Draft arguments distinguishing claim 1 from the cited prior art under 35 USC 102"
                </div>
              </div>
            </div>
          </div>

          {/* Summary Document */}
          <div className="border border-gray-200 rounded-lg p-4 bg-white">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 mb-1">Analysis & Summaries</h4>
                <p className="text-sm text-gray-600 mb-2">
                  Generate summaries and findings from multiple references
                </p>
                <div className="bg-gray-50 rounded p-2 text-xs font-mono text-gray-700">
                  "Summarize the key findings from these three prior art references"
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Feature Callout */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Sparkles className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-gray-900 mb-1">AI Context Integration</p>
            <p className="text-sm text-gray-600">
              When you add a draft to active documents, the AI chat automatically has access to it. This means AI can reference your draft content, suggest edits, and add new sections seamlessly.
            </p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        <Button
          onClick={handleGetStarted}
          className="flex-1 gap-2"
          size="lg"
        >
          Open Drafts
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
